import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { api } from '../api/client'
import type { Sonderfall } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import Kamera from '../komponenten/Kamera'
import SonderfallSchritte from '../komponenten/SonderfallSchritte'

/**
 * Schritt 2 von 3 im Sonderfall: das Gesamtfoto der Situation (P7-02). Im Ablauf geht es danach
 * mit der obersten Ebene weiter; aus der Sonderfall-Ansicht heraus ersetzt es nur das bisherige Foto.
 */
export default function SonderfallGesamtfoto() {
  const { sonderfallId } = useParams()
  const [suche] = useSearchParams()
  const imAblauf = suche.get('ablauf') === '1'
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const sonderfall = useQuery({
    queryKey: ['sonderfall', sonderfallId],
    queryFn: () => api.get<Sonderfall>(`/api/sonderfaelle/${sonderfallId}`),
  })

  const hochladen = useMutation({
    mutationFn: (bild: Blob) =>
      api.bild<Sonderfall>(`/api/sonderfaelle/${sonderfallId}/gesamtfoto`, bild, 'gesamt.jpg'),
    onSuccess: (aktualisiert) => {
      abfragen.setQueryData(['sonderfall', sonderfallId], aktualisiert)
      abfragen.invalidateQueries({ queryKey: ['versorgungspunkt', String(aktualisiert.versorgungspunktId)] })
      navigiere(
        imAblauf ? `/sonderfaelle/${sonderfallId}/ebenen/1?ablauf=1` : `/sonderfaelle/${sonderfallId}`,
        { replace: true },
      )
    },
  })

  const daten = sonderfall.data

  return (
    <AppRahmen
      titel={daten?.bezeichnung ?? 'Sonderfall'}
      unter={
        imAblauf
          ? 'Schritt 2 von 3 · Gesamtfoto'
          : daten?.gesamtfotoVorhanden
            ? 'Gesamtfoto ersetzen'
            : 'Gesamtfoto aufnehmen'
      }
      zurueck={`/sonderfaelle/${sonderfallId}`}
      pfad={daten?.pfad}
      ebene="sonderfall"
      pfadZusatz={daten ? `${daten.bezeichnung} · Gesamtfoto` : undefined}
    >
      {imAblauf && <SonderfallSchritte aktuell="gesamtfoto" />}

      <p className="sek">
        Die ganze Regalsituation ins Bild nehmen, alle Ebenen auf einmal. Die einzelnen Ebenen folgen danach.
      </p>

      {!imAblauf && daten?.gesamtfotoVorhanden && (
        <div className="gruppe">
          <span className="label">Bisheriges Foto</span>
          <img
            src={`/api/sonderfaelle/${sonderfallId}/gesamtfoto`}
            alt="Bisheriges Gesamtfoto"
            style={{ borderRadius: 8, border: '1px solid var(--linie)' }}
          />
        </div>
      )}

      <Kamera
        gesperrt={hochladen.isPending}
        hinweis={hochladen.isPending ? 'Wird gespeichert …' : undefined}
        beiAufnahme={(bild) => hochladen.mutate(bild)}
      />

      {hochladen.isError && <p className="hinweiszeile">{(hochladen.error as Error).message}</p>}
    </AppRahmen>
  )
}
